import { cn } from '@forest/shared'
import { Dialog as KobalteDialog } from '@kobalte/core/dialog'
import type { JSXElement } from 'solid-js'
import { Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import { Spinner } from '~/components/ui/spinner'

type CloseButtonOption = boolean | { action: () => void }

const [dialogState, setDialogState] = createStore<{
  isOpen: boolean
  isLoading: boolean
  title: string
  description: string | JSXElement
  descriptionClass?: string
  buttonText: string
  buttonAction?: () => void
  asyncAction?: () => Promise<void>
  closeButton?: CloseButtonOption
}>({
  isOpen: false,
  isLoading: false,
  title: 'TITLE',
  description: 'CONTENT',
  buttonText: 'BUTTON',
})

export const closeDialog = () => {
  setDialogState({ isOpen: false, isLoading: false })
}

export const showDialog = (payload: {
  title: string
  description: string | JSXElement
  descriptionClass?: string
  buttonText: string
  buttonAction?: () => void
  asyncAction?: () => Promise<void>
  closeButton?: CloseButtonOption
}) => {
  setDialogState({
    isOpen: true,
    isLoading: false,
    descriptionClass: undefined,
    buttonAction: undefined,
    asyncAction: undefined,
    closeButton: undefined,
    ...payload,
  })
}

export const setDialogLoading = (isLoading: boolean) => {
  setDialogState('isLoading', isLoading)
}

const runCloseAction = () => {
  const closeButton = dialogState.closeButton
  if (closeButton && typeof closeButton === 'object') {
    closeButton.action()
  }
}

export function Dialog() {
  const handleButtonClick = () => {
    if (dialogState.asyncAction) {
      dialogState.asyncAction()
    } else {
      dialogState.buttonAction?.()
      closeDialog()
    }
  }

  const hasCloseButton = () => Boolean(dialogState.closeButton)

  return (
    <KobalteDialog
      open={dialogState.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen && !dialogState.isLoading) {
          runCloseAction()
          closeDialog()
        }
      }}
    >
      <KobalteDialog.Portal>
        <KobalteDialog.Overlay class="fixed inset-0 z-[100] backdrop-blur-[12.5px] data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0" />
        <KobalteDialog.Content
          class="backdrop-filter-[50px] fixed left-1/2 top-1/2 z-[100] w-[calc(100vw-48px)] max-w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[32px] border-2 border-white/30 bg-white/75 p-6 data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          style={{
            'will-change': 'backdrop-filter',
            '--tw-enter-translate-x': '-50%',
            '--tw-enter-translate-y': '-50%',
            '--tw-exit-translate-x': '-50%',
            '--tw-exit-translate-y': '-50%',
          }}
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-2 text-center">
              <KobalteDialog.Title class="text-lg font-semibold text-[#333]">
                {dialogState.title}
              </KobalteDialog.Title>
              <p
                class={cn(
                  'whitespace-pre-line break-words text-base text-[#666]',
                  dialogState.descriptionClass,
                )}
              >
                {dialogState.description}
              </p>
            </div>
            <button
              type="button"
              class="flex h-[52px] w-full items-center justify-center rounded-xl bg-[#176361] text-base font-bold text-white active:bg-[#0D413F] disabled:bg-[#43807F]"
              disabled={dialogState.isLoading}
              onClick={handleButtonClick}
            >
              <Show
                when={!dialogState.isLoading}
                fallback={<Spinner class="size-6 text-[#176361]" />}
              >
                {dialogState.buttonText}
              </Show>
            </button>
          </div>

          <Show when={hasCloseButton() && !dialogState.isLoading}>
            <button
              type="button"
              class="absolute right-[-10px] top-[-10px] grid size-[42px] place-items-center overflow-hidden rounded-full backdrop-blur-[37.5px]"
              style={{ background: 'rgba(255, 255, 255, 0.56)' }}
              onClick={() => {
                runCloseAction()
                closeDialog()
              }}
            >
              <CloseSvg />
            </button>
          </Show>
        </KobalteDialog.Content>
      </KobalteDialog.Portal>
    </KobalteDialog>
  )
}

function CloseSvg() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.21429 4.71442L13.7857 13.2858"
        stroke="#333333"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.21427 13.2858L13.7856 4.71442"
        stroke="#333333"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  )
}
